import { useState } from "react";
import { Button } from "../../primitives/Button";
import { Select } from "../../primitives/Select";
import { Input } from "../../primitives/Input";
import { simulatePromotion } from "../../../api/catalog";
import type { Promotion } from "../../../api/catalog";
import type { PriceBook } from "../../../api/catalog";
import type { Product } from "../../../api/catalog";

type PromotionTabSimulationProps = {
  promotion: Promotion;
  priceBooks: PriceBook[];
  products: Product[];
};

export function PromotionTabSimulation({
  promotion,
  priceBooks,
  products,
}: PromotionTabSimulationProps) {
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [area, setArea] = useState("");
  const [priceBookId, setPriceBookId] = useState("");
  const [code, setCode] = useState(promotion.code || "");
  const [result, setResult] = useState<{
    base_price: number;
    promotions_applied: Array<{ name: string; discount_amount: number }>;
    discount_amount: number;
    final_price: number;
    explanation: Record<string, unknown>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSimulate = async () => {
    if (!productId) {
      setError("Sélectionnez un produit");
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await simulatePromotion({
        product_id: productId,
        price_book_id: priceBookId || undefined,
        quantity: parseFloat(quantity) || 1,
        area: area ? parseFloat(area) : undefined,
        code: code || undefined,
      });
      setResult({
        base_price: res.base_price,
        promotions_applied: res.promotions_applied.map((p) => ({
          name: p.name,
          discount_amount: p.discount_amount,
        })),
        discount_amount: res.discount_amount,
        final_price: res.final_price,
        explanation: res.explanation || {},
      });
    } catch (e) {
      setError((e as Error).message || "Erreur de simulation");
    } finally {
      setLoading(false);
    }
  };

  const copySummary = () => {
    if (!result) return;
    const text = [
      `Prix de base: ${result.base_price.toFixed(2)} CAD`,
      `Promotions appliquées: ${result.promotions_applied.map((p) => `${p.name} (-${p.discount_amount.toFixed(2)} CAD)`).join(", ") || "—"}`,
      `Rabais total: ${result.discount_amount.toFixed(2)} CAD`,
      `Prix final: ${result.final_price.toFixed(2)} CAD`,
    ].join("\n");
    void navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-text">Aperçu / Simulation</h3>
      <p className="text-xs text-muted">
        Simulez l'impact de la promotion sur un produit pour éviter les surprises.
      </p>

      <div className="rounded-md border border-border bg-surface p-4 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Select
              label="Produit"
              options={["Sélectionner...", ...products.map((p) => p.name)]}
              value={products.find((p) => p.id === productId)?.name ?? "Sélectionner..."}
              onChange={(label) => {
                const p = products.find((x) => x.name === label);
                setProductId(p?.id ?? "");
              }}
            />
          </div>
          <div>
            <Input
              label="Quantité"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div>
            <Input
              label="Superficie (si requis)"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />
          </div>
          <div>
            <Select
              label="PriceBook"
              options={["Auto", ...priceBooks.map((pb) => pb.name)]}
              value={priceBooks.find((pb) => pb.id === priceBookId)?.name ?? "Auto"}
              onChange={(label) => {
                const pb = priceBooks.find((x) => x.name === label);
                setPriceBookId(pb?.id ?? "");
              }}
            />
          </div>
          {promotion.code && (
            <div>
              <Input
                label="Code promo"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
          )}
        </div>
        <Button
          label={loading ? "Simulation..." : "Lancer la simulation"}
          intent="primary"
          size="sm"
          onClick={handleSimulate}
          disabled={loading}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>

      {result && (
        <div className="rounded-md border border-primary/20 bg-primary/5 p-4 space-y-3">
          <h4 className="text-sm font-medium text-text">Résultat</h4>
          <div className="text-sm space-y-1">
            <div>Prix de base: {result.base_price.toFixed(2)} CAD</div>
            <div>
              Promotions appliquées:{" "}
              {result.promotions_applied.length
                ? result.promotions_applied
                    .map((p) => `${p.name} (-${p.discount_amount.toFixed(2)} CAD)`)
                    .join(", ")
                : "—"}
            </div>
            <div>Rabais total: {result.discount_amount.toFixed(2)} CAD</div>
            <div className="font-medium">Prix final: {result.final_price.toFixed(2)} CAD</div>
          </div>
          <Button label="Copier le résumé" intent="default" size="sm" onClick={copySummary} />
        </div>
      )}
    </div>
  );
}
